/* ------------------------------------------------------------------ *
 * مار پله — WARM THE SCENE'S PICTURE before the table needs it
 * (card-86 round 2, batch P; the operator: «the board and game show
 * first … and the picture swaps in once it has arrived» — this makes
 * «once it has arrived» usually «already»).
 *
 * ONE `new Image()` per picture, at most once per page life, when the
 * «مار پله» chooser opens (Lobby.tsx) or a «مار پله» table link lands
 * (`boot/open-route.ts`) — never from the lobby's own render or idle, so
 * the heat gate's `lobby-idle` reads the same. The fetch is the SAME
 * hashed `/assets/` URL the scene's `<img>` requests (immutable, so the
 * table's request is a cache hit), in the format the scene will pick:
 * the WebP when the picture has one AND this browser decodes WebP (a
 * 1×1 lossy decode, asked once and cached in a module variable), else
 * the JPEG — the same choice a `<picture>` makes.
 *
 * The pictures are FOUND, not listed: `scene-bg.*` is «جنگل»'s (its
 * round-1 name), `scene-<style>.*` any other picture style's — so a new
 * picture (or a WebP twin the art tools write) is picked up with no
 * code change, and a style with no picture («اورجینال») warms nothing.
 * ------------------------------------------------------------------ */

const FOUND = import.meta.glob<string>("../../assets/ladders/scene-*.{jpg,webp}", {
  eager: true,
  query: "?url",
  import: "default",
});

export interface ScenePictureSources {
  readonly jpg: string;
  readonly webp?: string;
}

let found: Readonly<Record<string, string>> = FOUND;

/** A picture style's background files, or `null` when the style has none. */
export function sceneSources(style: string): ScenePictureSources | null {
  const base = `../../assets/ladders/${style === "jungle" ? "scene-bg" : `scene-${style}`}`;
  const jpg = found[`${base}.jpg`];
  if (!jpg) return null;
  const webp = found[`${base}.webp`];
  return webp ? { jpg, webp } : { jpg };
}

/** The smallest lossy WebP (1×1) — the format the scene would be served in. */
const WEBP_PROBE =
  "data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA";
let webpSupport: Promise<boolean> | null = null;

/** Whether this browser decodes WebP — asked ONCE per page life, then cached. */
export function supportsWebp(): Promise<boolean> {
  if (!webpSupport) {
    webpSupport = new Promise<boolean>((resolve) => {
      if (typeof Image !== "function") {
        resolve(false);
        return;
      }
      const probe = new Image();
      probe.onload = () => resolve(probe.width === 1 && probe.height === 1);
      probe.onerror = () => resolve(false);
      probe.src = WEBP_PROBE;
    });
  }
  return webpSupport;
}

/** The URLs already asked for this page life (each picture once, whichever door asked). */
const warmed = new Set<string>();
/** Held until each fetch settles, so nothing collects a preload mid-flight. */
const inFlight = new Set<HTMLImageElement>();

function fetchOnce(url: string): void {
  if (warmed.has(url) || typeof Image !== "function") return;
  warmed.add(url);
  const img = new Image();
  img.decoding = "async";
  const done = (): void => {
    inFlight.delete(img);
  };
  img.onload = done;
  img.onerror = done;
  inFlight.add(img);
  img.src = url;
}

/**
 * Warm `style`'s background (the table's look when it is known; «جنگل» when it is not — a link
 * cannot say which look its table has). Never throws, never repeats a fetch.
 */
export function prefetchLaddersScene(style = "jungle"): void {
  const src = sceneSources(style) ?? sceneSources("jungle");
  if (!src) return;
  if (!src.webp) {
    fetchOnce(src.jpg);
    return;
  }
  const webp = src.webp;
  void supportsWebp().then((ok) => fetchOnce(ok ? webp : src.jpg));
}

// ------------------------------------------------------------------------------------ test seams

/** Replace the found files (`{ "<glob key>": url }`) and forget every cached answer. */
export function __setSceneFilesForTests(next: Readonly<Record<string, string>> | null): void {
  found = next ?? FOUND;
  webpSupport = null;
  warmed.clear();
  inFlight.clear();
}
